import { getFormatDateFromObject, getDateFromString } from 'shared-components';
import { IRisk, FamilySponsorType } from 'types';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { isFundingRisk } from '../../../../utilities/risks';
import { bankStatementName } from '../../../../utilities/documents';
import { formatCurrency } from '../../../../utilities/formatting';

interface Props {
  id: string;
  sponsorType: FamilySponsorType;
  showRisks: boolean;
  risk: IRisk['financeDocsRisk']['familySponsorship'];
  isPaperBankStatement?: boolean;
  isLogoShown?: boolean;
  isDateShown?: boolean;
  isNameAccShown?: boolean;
  isInEnglish?: boolean;
  hasProvidedTranslation?: boolean;
  currency?: string;
  hasAmountInAccount?: boolean;
  belowReqAmount?: boolean;
  dateBelowReqAmount?: string;
  hasRunningTransactions?: boolean;
  hasClosingBalanceDate?: boolean;
  dateOfClosingBalance?: string;
  uploadedBy?: string;
  sponsorshipAmount: number;
}

export default function BankStatement({
  id,
  sponsorType,
  showRisks,
  risk,
  isPaperBankStatement,
  isLogoShown,
  isDateShown,
  isNameAccShown,
  isInEnglish,
  hasProvidedTranslation,
  currency,
  hasAmountInAccount,
  belowReqAmount,
  dateBelowReqAmount,
  hasRunningTransactions,
  hasClosingBalanceDate,
  dateOfClosingBalance,
  uploadedBy,
  sponsorshipAmount,
}: Props) {
  if (
    isPaperBankStatement === undefined &&
    isLogoShown === undefined &&
    isDateShown === undefined &&
    isNameAccShown === undefined &&
    isInEnglish === undefined &&
    hasAmountInAccount === undefined &&
    belowReqAmount === undefined &&
    hasRunningTransactions === undefined &&
    hasClosingBalanceDate === undefined
  ) {
    return <div>Uploaded by {uploadedBy ? uploadedBy : 'University'}</div>;
  }
  const docName = bankStatementName(sponsorType);
  const amount = `£${formatCurrency(sponsorshipAmount)}`;
  return (
    <>
      <SummaryRow
        key="isPaperBankStatement"
        label="Statement Type"
        fullLabel={`Is the ${docName.toLowerCase()} a paper statement from the bank or did you get it from the internet?`}
        value={isPaperBankStatement ? 'Paper' : 'Internet'}
        isRisk={showRisks && isFundingRisk(risk, 'isPaperBankStatement', id)}
      />

      <SummaryRow
        key="isLogoShown"
        label="Logo Shown"
        fullLabel="Does the statement show the logo of the bank?"
        value={isLogoShown ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'isLogoShown', id)}
      />

      <SummaryRow
        key="isDateShown"
        label="Date Shown"
        fullLabel="Does the statement show a date at the top of the page?"
        value={isDateShown ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'isDateShown', id)}
      />

      <SummaryRow
        key="isNameAccShown"
        label="Name and Account Number"
        fullLabel="Does the statement show the name and the account number?"
        value={isNameAccShown ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'isNameAccShown', id)}
      />

      <SummaryRow
        key="isInEnglish"
        label="In English"
        fullLabel="Is the statement in English?"
        value={isInEnglish ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'inEnglishOrHasTranslation', id)}
      />

      {!isInEnglish && (
        <SummaryRow
          key="hasProvidedTranslation"
          label="Translation Provided"
          fullLabel={`Have you provided an English translation of the ${docName.toLowerCase()}?`}
          value={hasProvidedTranslation ? 'Yes' : 'No'}
          isRisk={showRisks && isFundingRisk(risk, 'inEnglishOrHasTranslation', id)}
        />
      )}

      <SummaryRow key="currency" label="Currency" fullLabel="What is the currency?" value={currency || 'N/A'} />

      <SummaryRow
        key="hasAmountInAccount"
        label="Has Amount in Account"
        fullLabel={`Is there ${amount} in the account now?`}
        value={hasAmountInAccount ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasAmountInAccount', id)}
      />

      {hasAmountInAccount && (
        <SummaryRow
          key="belowReqAmount"
          label="Has Amount been Below Required"
          fullLabel={`Was the account balance below ${amount} at any time in the past 60 days?`}
          value={belowReqAmount ? 'Yes' : 'No'}
          isRisk={showRisks && isFundingRisk(risk, 'belowReqAmount', id)}
        />
      )}

      {belowReqAmount && (
        <SummaryRow
          key="dateBelowReqAmount"
          label="Maturity Start Date"
          fullLabel={`When was the last time the balance was below ${amount}?`}
          value={getFormatDateFromObject(getDateFromString(dateBelowReqAmount!))}
        />
      )}

      <SummaryRow
        key="hasRunningTransactions"
        label="Has Running Transactions"
        fullLabel="Does the statement show a running total alongside all transactions in the past 28 days?"
        value={hasRunningTransactions ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasRunningTransactions', id)}
      />

      <SummaryRow
        key="hasClosingBalanceDate"
        label="Has Closing Balance Date"
        fullLabel="Does the closing balance at the bottom of the statement have a date alongside it?"
        value={hasClosingBalanceDate ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasClosingBalanceDate', id)}
      />

      {hasClosingBalanceDate && (
        <SummaryRow
          key="dateOfClosingBalance"
          label="Closing Balance Date"
          fullLabel="What is the date next to the closing balance?"
          value={getFormatDateFromObject(getDateFromString(dateOfClosingBalance))}
          isRisk={showRisks && isFundingRisk(risk, 'dateOfClosingBalance', id)}
        />
      )}
    </>
  );
}
